import 'app/containers/MonitoringPage/style.scss';

import React, { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { addDays } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import { Container, Icon, Table } from 'semantic-ui-react';

import { IsPACSAssociatedContext } from 'app/components/PACSCommunicationProvider';
import StudyRow from 'app/components/PACSQuery/StudyRow';
import { SearchParams } from 'app/components/PACSQuery/SearchParams';
import PACSStudySearch, { PACSStudySearchProps } from 'app/components/PACSQuery/PACSStudySearch';
import { checkIsPicoxiaDicom, getDicomValue } from 'app/utils/dicom/DicomData';
import PACSConfiguration from 'app/components/PACSConfiguration';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import queryPACSStudiesQueryOptions from 'app/queries/queryPACSStudiesQueryOptions';
import { useDebounceValue } from 'usehooks-ts';
import getStudiesQueryOptions from 'app/queries/getStudiesQueryOptions';
import StudyStoreContext from 'app/providers/StudyStoreProvider/context';
import { useDispatch, useSelector } from 'react-redux';
import {
  PACSConfigurationState,
  selectPACSConfiguration,
} from 'app/redux/PACSConfiguration/reducer';
import PACSCommunicationInjector from 'app/injectors/PACSCommunication/context';
import { setPACSConfiguration } from 'app/redux/PACSConfiguration/reducer';
import getStudyQueryOptions, { GetStudyParams } from 'app/queries/getStudyQueryOptions';
import { WithRouterProps, withRouter } from 'react-router';

function PACSQueryImplWithRouterInjected({ router }: WithRouterProps) {
  const intl = useIntl();
  const studyStore = useContext(StudyStoreContext);
  const queryClient = useQueryClient();

  const [defaultDate, setDefaultDate] = useState<Date>(() => addDays(new Date(), -7));
  const [pendingSearchParams, setPendingSearchParams] = useState({});
  const [selectedStudyInstanceUID, setSelectedStudyInstanceUID] = useState<string>(undefined);
  const [multiSelectedInstanceUIDs, setMultiSelectedInstanceUIDs] = useState<
    Record<string, string | undefined>
  >({});

  const [searchParams, setDebouncedSearchParams] = useDebounceValue<SearchParams>(
    pendingSearchParams,
    500
  );

  const pendingParamsWithDefault = _.isEmpty(_.filter(pendingSearchParams))
    ? { searchStartDate: defaultDate }
    : pendingSearchParams;
  const searchParamsWithDefault = _.isEmpty(_.filter(searchParams))
    ? { searchStartDate: defaultDate }
    : searchParams;

  const { data: studies, promise } = useQuery({
    ...queryPACSStudiesQueryOptions(searchParamsWithDefault),
    experimental_prefetchInRender: true,
  });

  const [prevStudies, setPrevStudies] = useState(studies);

  if (studies !== prevStudies && studies !== undefined) {
    // Kind of a strange pattern should here : https://react.dev/learn/you-might-not-need-an-effect
    // useEffect should be avoid for state update it seems :/
    setPrevStudies(studies);

    const filteredValues = _.pick(
      multiSelectedInstanceUIDs,
      _.filter(_.map(studies, (study) => getDicomValue(study?.images?.[0]?.StudyInstanceUID)))
    );

    setMultiSelectedInstanceUIDs(filteredValues);
  }

  const onMultiSelectChange = useCallback(
    (studyInstanceUID: string, checked: boolean, picoxiaStudyId?: string) => {
      setMultiSelectedInstanceUIDs((prev) => {
        if (checked) {
          return {
            ...prev,
            [studyInstanceUID]: picoxiaStudyId,
          };
        }
        return _.omit(prev, studyInstanceUID);
      });
    },
    [setMultiSelectedInstanceUIDs]
  );

  const openMultiStudies = useCallback(() => {
    const concatenatedSearchString = _.map(
      multiSelectedInstanceUIDs,
      (picoxiaStudyId, studyInstanceUID) => {
        const studyInfoParams: GetStudyParams = { studyInstanceUID };
        if (picoxiaStudyId) studyInfoParams.studyID = picoxiaStudyId;

        // Invalidation with 'none' is required here, else invalidation happens will queries are still active
        // thus they get instantly fetched again and we do not refetch them after navigation has happened.
        queryClient.invalidateQueries({
          ...getStudyQueryOptions(studyInfoParams),
          refetchType: 'none',
        });
        queryClient.invalidateQueries({ queryKey: [{ query: '/studies' }], refetchType: 'none' });

        if (picoxiaStudyId) {
          return `study_ids[]=${picoxiaStudyId}`;
        } else {
          return `studyInstanceUIDs[]=${studyInstanceUID}`;
        }
      }
    ).join('&');
    router.push(`/viewer?${concatenatedSearchString}`);
  }, [multiSelectedInstanceUIDs, queryClient]);

  useEffect(() => {
    let active = true;
    promise.then((studies) => {
      if (!active) return;

      const studiesPrefetchParams = studies?.map((study) => {
        const referenceImage = study.images[0];
        const isPicoxiaStudy = checkIsPicoxiaDicom(referenceImage);
        const StudyID = getDicomValue(referenceImage.StudyID);
        const StudyInstanceUID = getDicomValue(referenceImage.StudyInstanceUID);
        const params: GetStudyParams = { studyInstanceUID: StudyInstanceUID };
        if (isPicoxiaStudy) params.studyID = StudyID;

        return params;
      });
      if (!studiesPrefetchParams || studiesPrefetchParams.length === 0) return;
      queryClient.prefetchQuery({
        ...getStudiesQueryOptions(studiesPrefetchParams, { studyStore, queryClient }),
        staleTime: Infinity,
      });
    });
    return () => {
      active = false;
    };
  }, [promise]);

  useEffect(() => {
    setDebouncedSearchParams(pendingSearchParams);
  }, [pendingSearchParams, setDebouncedSearchParams]);

  return (
    <div className="monitoring-background dark-mode">
      <PACSStudySearch
        searchParams={pendingParamsWithDefault}
        setSearchParams={setPendingSearchParams as PACSStudySearchProps['setSearchParams']}
      />

      <div className="monitoring-table">
        <Table collapsing={false} inverted className="table-colors">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="multi-select-header-cell">
                <div className="actions__icon">
                  <Icon
                    className="eye icon picoxia-fix"
                    title={intl.formatMessage({ id: 'monitoring.study.compare_multiple' })}
                    aria-label={intl.formatMessage({ id: 'monitoring.study.compare_multiple' })}
                    onClick={openMultiStudies}
                  />
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '10%' }}>
                <FormattedMessage id="monitoring.date" />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '8%' }}>
                <FormattedMessage id="monitoring.id" />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '8%' }}>
                <FormattedMessage id="monitoring.owner" />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '8%' }}>
                <FormattedMessage id="monitoring.animal" />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '5%' }}>
                <FormattedMessage id="monitoring.age" />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '5%' }}>
                <FormattedMessage id="monitoring.sex" />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '5%' }}>
                <FormattedMessage id="monitoring.images" />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '45%' }}>
                <FormattedMessage id="monitoring.patterns" />
              </Table.HeaderCell>
              <Table.HeaderCell style={{ width: '4%' }} />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {studies?.map((study) => {
              const studyInstanceUID = getDicomValue(study?.images[0]?.StudyInstanceUID) as string;
              return (
                <StudyRow
                  key={studyInstanceUID}
                  onClick={() => setSelectedStudyInstanceUID(studyInstanceUID)}
                  selected={selectedStudyInstanceUID === studyInstanceUID}
                  isMultiSelected={studyInstanceUID in multiSelectedInstanceUIDs}
                  onMultiSelectChange={(checked: boolean, picoxiaStudyId?: string) =>
                    onMultiSelectChange(studyInstanceUID, checked, picoxiaStudyId)
                  }
                  {...study}
                />
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
}

const PACSQueryImpl = withRouter(PACSQueryImplWithRouterInjected);

export default function PACSQuery() {
  const isPACSAssociated = useContext(IsPACSAssociatedContext);
  const PACSCommunicationCtor = useContext(PACSCommunicationInjector);
  const pacsConfiguration = useSelector(selectPACSConfiguration);
  const dispatch = useDispatch();

  const onAssociationSuccess = useCallback(
    (associationParameters: PACSConfigurationState) => {
      dispatch(setPACSConfiguration({ ...associationParameters, enabled: true }));
    },
    [dispatch]
  );
  if (!isPACSAssociated) {
    if (PACSCommunicationCtor) {
      return (
        <Container>
          <PACSConfiguration
            initialValue={pacsConfiguration}
            onAssociationSuccess={onAssociationSuccess}
            minimumAssociationDuration={1000}
          />
        </Container>
      );
    } else {
      return (
        <div className="monitoring-background dark-mode">
          <div className="pacs-not-configured-warning">
            <h2>
              <FormattedMessage id="pacs.unavailable_for_app.title" />
            </h2>
            <p>
              <FormattedMessage id="pacs.unavailable_for_app" />
            </p>
          </div>
        </div>
      );
    }
  }

  return <PACSQueryImpl />;
}
